/* Texas-e-parts theme JS
 * - YMM cascading dropdowns (hero, garage modal, fitment results page)
 * - Garage feature with localStorage persistence
 * - Native Shopify cart via /cart.js endpoints
 * - Fitment results page rendering
 */

(function () {
  'use strict';

  // ---- i18n bridge (added for Spanish localization 2026-06) ----
  // Localized strings are injected by theme.liquid (window.theme.strings); English baked in as fallback.
  var EN_STRINGS = {
    ymm_year: "Year", ymm_make: "Make", ymm_model: "Model",
    ymm_need_year_make: "Pick at least a Year and Make to filter parts.",
    ymm_need_full: "Pick Year, Make, and Model first.",
    garage_empty: "No saved vehicles yet. Add one below — we'll remember it next time you visit.",
    garage_chip_count: "My Garage (%count%)", garage_currently_active: "Currently Active",
    garage_saved: "Saved", garage_active: "Active", garage_use: "Use", garage_delete: "Delete",
    garage_remove_confirm: "Remove this vehicle from your garage?",
    garage_added: "Added %vehicle% to your garage!",
    cart_empty: "Your cart is empty", cart_empty_cta: "Find your parts above.",
    cart_adding: "Adding…", cart_added: "Added ✓", cart_error: "Error", cart_add: "Add", cart_remove: "Remove",
    results_heading_default: "Find parts for your vehicle", results_eyebrow_default: "Fitment Search",
    results_heading_prefix: "Parts for %vehicle%", results_eyebrow: "Fitment Results",
    results_count: "%count% PARTS", results_loading: "Loading parts…",
    results_load_error: "Unable to load fitment data.",
    results_no_match_title: "No parts match this vehicle yet",
    results_no_match_body: "Try a different year or model, or browse all parts.",
    results_in_stock_count: "In stock (%count%)", results_out_stock_count: "Out of stock (%count%)",
    results_nothing_title: "Nothing here",
    results_nothing_body_in: "No in-stock parts for this vehicle. Switch back to All.",
    results_nothing_body_out: "No out-of-stock parts for this vehicle. Switch back to All.",
    product_sold_out: "Sold out", product_sale: "Sale", product_save: "Save %amount%",
    product_add_to_cart: "Add to cart", product_notify_me: "Notify me", product_no_image: "No image"
  };
  var I18N = Object.assign({}, EN_STRINGS, (window.theme && window.theme.strings) || {});
  function t(key, vars){
    var s = I18N[key] != null ? I18N[key] : '';
    if (vars){ for (var k in vars){ s = s.split('%' + k + '%').join(vars[k]); } }
    return s;
  }

  // ============================================================
  // Storage helper with in-memory fallback
  // ============================================================
  const _mem = {};
  const Store = {
    get(k) {
      try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; }
      catch (e) { return _mem[k] || null; }
    },
    set(k, v) {
      try { localStorage.setItem(k, JSON.stringify(v)); }
      catch (e) { _mem[k] = v; }
    }
  };

  // ============================================================
  // State
  // ============================================================
  const state = {
    fitmentIndex: null,
    garage: Store.get('txep_garage') || [],
    activeVehicleId: Store.get('txep_active_vehicle') || null,
    deliveryMethod: 'pickup'
  };

  // ============================================================
  // Fitment Index loader
  // ============================================================
  let _fitmentLoadPromise = null;
  function loadFitmentIndex() {
    if (state.fitmentIndex) return Promise.resolve(state.fitmentIndex);
    if (_fitmentLoadPromise) return _fitmentLoadPromise;
    const url = (window.theme && window.theme.fitmentIndexUrl) ||
                document.querySelector('script[data-fitment-index-url]')?.dataset.fitmentIndexUrl ||
                '/assets/fitment-index.json';
    _fitmentLoadPromise = fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Fitment index ' + r.status)))
      .then(d => { state.fitmentIndex = d; return d; })
      .catch(err => {
        console.error('Failed to load fitment index:', err);
        return null;
      });
    return _fitmentLoadPromise;
  }

  // ============================================================
  // Search Meta loader (handle -> {t:title, ty:type, pp:primaryPart, mk:make})
  // Powers fitment-aware search results + predictive autocomplete.
  // ============================================================
  let _searchMetaPromise = null;
  let _searchMetaByHandle = null;
  function loadSearchMeta() {
    if (_searchMetaByHandle) return Promise.resolve(_searchMetaByHandle);
    if (_searchMetaPromise) return _searchMetaPromise;
    const url = (window.theme && window.theme.searchMetaUrl) ||
                document.querySelector('script[data-search-meta-url]')?.dataset.searchMetaUrl ||
                '/assets/search-meta.json';
    _searchMetaPromise = fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('search-meta ' + r.status)))
      .then(arr => { const byH = {}; for (const m of arr) byH[m.h] = m; _searchMetaByHandle = byH; return byH; })
      .catch(err => { console.error('Failed to load search meta:', err); return null; });
    return _searchMetaPromise;
  }

  // ============================================================
  // YMM Finder - initialize all [data-ymm-finder] blocks
  // ============================================================
  function initYMMFinders() {
    document.querySelectorAll('[data-ymm-finder]').forEach(finder => {
      if (finder.dataset.ymmInit === '1') return;   // idempotent: gate/narrow finders are wired after injection
      finder.dataset.ymmInit = '1';
      const yearSel = finder.querySelector('[data-ymm-year]');
      const makeSel = finder.querySelector('[data-ymm-make]');
      const modelSel = finder.querySelector('[data-ymm-model]');
      const submitBtn = finder.querySelector('[data-ymm-submit]');
      const saveBtn = finder.querySelector('[data-ymm-save-to-garage]');
      const saveVehicleBtn = finder.querySelector('[data-save-vehicle]');

      loadFitmentIndex().then(idx => {
        if (!idx) return;
        // Populate years
        idx.years.forEach(y => {
          const o = document.createElement('option');
          o.value = y; o.textContent = y;
          yearSel.appendChild(o);
        });

        yearSel.addEventListener('change', () => {
          makeSel.innerHTML = '<option value="">' + t('ymm_make') + '</option>';
          modelSel.innerHTML = '<option value="">' + t('ymm_model') + '</option>';
          modelSel.disabled = true;
          if (saveVehicleBtn) saveVehicleBtn.disabled = true;
          if (!yearSel.value) { makeSel.disabled = true; return; }
          (idx.yearMakes[yearSel.value] || []).forEach(m => {
            const o = document.createElement('option');
            o.value = m; o.textContent = m;
            makeSel.appendChild(o);
          });
          makeSel.disabled = false;
          // Sidebar context: preselect the make implied by the page ("Toyota" collection, "toyota camry" search, "Dodge & Ram").
          const def = (finder.dataset.defaultMake || '').toLowerCase();
          if (def && !makeSel.value) {
            const hit = Array.from(makeSel.options).find(o => o.value && def.indexOf(o.value.toLowerCase()) !== -1);
            if (hit) { makeSel.value = hit.value; makeSel.dispatchEvent(new Event('change')); }
          }
        });

        makeSel.addEventListener('change', () => {
          modelSel.innerHTML = '<option value="">' + t('ymm_model') + '</option>';
          if (saveVehicleBtn) saveVehicleBtn.disabled = true;
          if (!makeSel.value || !yearSel.value) { modelSel.disabled = true; return; }
          const models = (idx.yearMakeModels[yearSel.value] || {})[makeSel.value] || [];
          models.forEach(m => {
            const o = document.createElement('option');
            o.value = m; o.textContent = m;
            modelSel.appendChild(o);
          });
          modelSel.disabled = false;
        });

        modelSel.addEventListener('change', () => {
          if (saveVehicleBtn) saveVehicleBtn.disabled = !modelSel.value;
        });
      });

      if (submitBtn) {
        submitBtn.addEventListener('click', e => {
          e.preventDefault();
          if (!yearSel.value || !makeSel.value) {
            alert(t('ymm_need_year_make'));
            return;
          }
          // EVERY finder routes into the unified fitment-aware search; target='search'
          // additionally carries a part keyword (sidebar/gate finders narrowing a query).
          if (finder.dataset.ymmTarget === 'search') {
            const kw = finder.dataset.keyword || '';
            const q = [yearSel.value, makeSel.value, modelSel.value, kw].filter(Boolean).join(' ');
            window.location.href = '/search?type=product&q=' + encodeURIComponent(q);
            return;
          }
          // Default: route to the unified fitment-aware search (grouped, uncapped) — same engine
          // as the search/sidebar finders. Retires the legacy /pages/fitment 48-cap renderer.
          const q = [yearSel.value, makeSel.value, modelSel.value].filter(Boolean).join(' ');
          window.location.href = '/search?type=product&q=' + encodeURIComponent(q);
        });
      }

      if (saveBtn) {
        saveBtn.addEventListener('click', e => {
          e.preventDefault();
          if (!yearSel.value || !makeSel.value || !modelSel.value) {
            alert(t('ymm_need_full'));
            return;
          }
          saveVehicle(yearSel.value, makeSel.value, modelSel.value, '');
          openGarage();
        });
      }

      if (saveVehicleBtn) {
        saveVehicleBtn.addEventListener('click', e => {
          e.preventDefault();
          if (!yearSel.value || !makeSel.value || !modelSel.value) return;
          const trimSel = finder.querySelector('[data-ymm-trim]');
          saveVehicle(yearSel.value, makeSel.value, modelSel.value, trimSel?.value || '');
          yearSel.value = ''; yearSel.dispatchEvent(new Event('change'));
          saveVehicleBtn.disabled = true;
        });
      }
    });
  }

  // ============================================================
  // Garage
  // ============================================================
  function vehicleLabel(v) {
    return v.year + ' ' + v.make + ' ' + v.model + (v.trim ? ' ' + v.trim : '');
  }

  function saveVehicle(year, make, model, trim) {
    const id = year + '|' + make + '|' + model + '|' + (trim || '');
    if (state.garage.find(v => v.id === id)) return;
    state.garage.push({ id, year, make, model, trim: trim || '' });
    Store.set('txep_garage', state.garage);
    renderGarageChip();
    renderGarageList();
  }

  function deleteVehicle(id) {
    state.garage = state.garage.filter(v => v.id !== id);
    if (state.activeVehicleId === id) {
      state.activeVehicleId = null;
      Store.set('txep_active_vehicle', null);
    }
    Store.set('txep_garage', state.garage);
    renderGarageChip();
    renderGarageList();
  }

  function useVehicle(id) {
    const v = state.garage.find(x => x.id === id);
    if (!v) return;
    state.activeVehicleId = id;
    Store.set('txep_active_vehicle', id);
    closeGarage();
    // Use the same unified fitment-aware search engine as the YMM finders (grouped, uncapped),
    // instead of the legacy /pages/fitment page (flat grid capped at 48 results).
    const q = [v.year, v.make, v.model].filter(Boolean).join(' ');
    window.location.href = '/search?type=product&q=' + encodeURIComponent(q);
  }

  function renderGarageChip() {
    const chip = document.querySelector('[data-garage-chip]');
    const txt = document.querySelector('[data-garage-chip-text]');
    if (!chip || !txt) return;
    if (state.garage.length === 0) { chip.style.display = 'none'; return; }
    chip.style.display = 'inline-flex';
    const active = state.garage.find(v => v.id === state.activeVehicleId);
    txt.textContent = active ? vehicleLabel(active) : t('garage_chip_count', {count: state.garage.length});
  }

  function renderGarageList() {
    const list = document.querySelector('[data-garage-list]');
    if (!list) return;
    if (state.garage.length === 0) {
      list.innerHTML = '<div class="garage-empty">' + t('garage_empty') + '</div>';
      return;
    }
    list.innerHTML = state.garage.map(v => {
      const isActive = v.id === state.activeVehicleId;
      return '<div class="garage-vehicle' + (isActive ? ' active' : '') + '">' +
        '<div>' +
          '<div class="garage-vehicle-name">' + vehicleLabel(v) + '</div>' +
          '<div class="garage-vehicle-meta">' + (isActive ? t('garage_currently_active') : t('garage_saved')) + '</div>' +
        '</div>' +
        '<div class="garage-vehicle-actions">' +
          '<button class="use-btn" data-use-vehicle="' + v.id + '">' + (isActive ? t('garage_active') : t('garage_use')) + '</button>' +
          '<button class="delete-btn" data-delete-vehicle="' + v.id + '">' + t('garage_delete') + '</button>' +
        '</div>' +
      '</div>';
    }).join('');
  }

  function openGarage() {
    renderGarageList();
    document.querySelector('[data-garage-overlay]')?.classList.add('open');
  }
  function closeGarage() {
    document.querySelector('[data-garage-overlay]')?.classList.remove('open');
  }

  function bindGarage() {
    document.querySelectorAll('[data-open-garage]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); openGarage(); }));
    document.querySelector('[data-garage-chip]')?.addEventListener('click', openGarage);
    document.querySelector('[data-close-garage]')?.addEventListener('click', closeGarage);
    document.querySelector('[data-garage-overlay]')?.addEventListener('click', e => {
      if (e.target.dataset.garageOverlay !== undefined) closeGarage();
    });
    // Event delegation for use/delete buttons
    document.querySelector('[data-garage-list]')?.addEventListener('click', e => {
      const useId = e.target.dataset.useVehicle;
      const delId = e.target.dataset.deleteVehicle;
      if (useId) useVehicle(useId);
      if (delId && confirm(t('garage_remove_confirm'))) deleteVehicle(delId);
    });
  }

  // ============================================================
  // Save vehicle from product page
  // ============================================================
  function bindSaveProductFitment() {
    document.querySelectorAll('[data-save-product-fitment]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const tags = btn.dataset.tags?.split('|') || [];
        // Vendor is the store name ("Texas-e-parts") for every product, so it is NOT the make.
        // Prefer the real make derived server-side (data-make); fall back to vendor only if absent.
        const make = btn.dataset.make || btn.dataset.vendor;
        if (!tags.length) return;
        // Parse the first fitment tag: "Highlander 2020-2025" or "F-Series 2015-2023"
        for (const t of tags) {
          const m = t.match(/^(.+?)\s+(\d{4})(?:-(\d{4}))?$/);
          if (m) {
            const model = m[1].trim();
            const y2 = parseInt(m[3] || m[2]);
            saveVehicle(String(y2), make, model, '');
            alert(t('garage_added', {vehicle: y2 + ' ' + make + ' ' + model}));
            break;
          }
        }
      });
    });
  }

  // ============================================================
  // Shopify Cart - uses /cart.js endpoints
  // ============================================================
  function fmtMoney(cents) {
    // Format cents as $X.XX; honor Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json());
  }

  function addToCart(variantId, qty) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] })
    }).then(r => r.json());
  }

  function updateCart(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(r => r.json());
  }

  function renderCart(cart) {
    const itemsEl = document.querySelector('[data-cart-items]');
    const countEl = document.querySelector('[data-cart-count]');
    const subtotalEl = document.querySelector('[data-cart-subtotal]');
    const totalEl = document.querySelector('[data-cart-total]');
    const deliveryEl = document.querySelector('[data-delivery-options]');
    const footEl = document.querySelector('[data-cart-foot]');

    if (countEl) countEl.textContent = cart.item_count;

    if (!itemsEl) return;
    if (cart.item_count === 0) {
      itemsEl.innerHTML = '<div class="cart-empty">' + t('cart_empty') + '<br/>' + t('cart_empty_cta') + '</div>';
      if (deliveryEl) deliveryEl.style.display = 'none';
      if (footEl) footEl.style.display = 'none';
      return;
    }

    itemsEl.innerHTML = cart.items.map((item, i) => {
      const img = item.image ? '<img src="' + item.image + '" alt="">' : '';
      return '<div class="sf-line">' +
        '<div class="sf-line__img">' + img + '</div>' +
        '<div class="sf-line__info">' +
          '<a href="' + item.url + '" class="sf-line__title">' + item.product_title + '</a>' +
          '<div class="sf-line__sku">#' + (item.sku || '') + '</div>' +
          '<div class="sf-line__row">' +
            '<div class="tep-qty">' +
              '<button class="tep-qty__btn" data-cart-qty="' + (i+1) + '" data-delta="-1">\u2212</button>' +
              '<span class="tep-qty__val" style="display:flex;align-items:center;justify-content:center">' + item.quantity + '</span>' +
              '<button class="tep-qty__btn" data-cart-qty="' + (i+1) + '" data-delta="1">+</button>' +
            '</div>' +
            '<button class="sf-line__rm" data-cart-qty="' + (i+1) + '" data-delta="-' + item.quantity + '">' + t('cart_remove') + '</button>' +
          '</div>' +
        '</div>' +
        '<div class="sf-line__price">' + fmtMoney(item.final_line_price) + '</div>' +
      '</div>';
    }).join('');

    if (deliveryEl) deliveryEl.style.display = 'block';
    if (footEl) footEl.style.display = 'block';
    if (subtotalEl) subtotalEl.textContent = fmtMoney(cart.items_subtotal_price);
    if (totalEl) totalEl.textContent = fmtMoney(cart.total_price);
  }

  function openCart() {
    fetchCart().then(c => {
      renderCart(c);
      document.querySelector('[data-overlay]')?.classList.add('open');
      document.querySelector('[data-cart-drawer]')?.classList.add('open');
    });
  }

  function closeCart() {
    document.querySelector('[data-overlay]')?.classList.remove('open');
    document.querySelector('[data-cart-drawer]')?.classList.remove('open');
  }

  function bindCart() {
    // Open/close
    document.querySelectorAll('[data-open-cart]').forEach(b => {
      b.addEventListener('click', e => { e.preventDefault(); openCart(); });
    });
    document.querySelector('[data-close-cart]')?.addEventListener('click', closeCart);
    document.querySelector('[data-overlay]')?.addEventListener('click', closeCart);

    // Delivery method radios
    document.querySelectorAll('[data-delivery-method]').forEach(el => {
      el.addEventListener('click', () => {
        document.querySelectorAll('[data-delivery-method]').forEach(o => o.classList.remove('selected'));
        el.classList.add('selected');
        state.deliveryMethod = el.dataset.deliveryMethod;
      });
    });

    // Quick-add from product cards
    document.body.addEventListener('click', e => {
      if (e.target.matches('[data-quick-add]')) {
        e.preventDefault();
        const btn = e.target;
        const variantId = btn.dataset.variantId;
        btn.textContent = t('cart_adding');
        btn.disabled = true;
        addToCart(variantId, 1)
          .then(() => {
            btn.textContent = t('cart_added');
            btn.classList.add('added');
            return fetchCart().then(c => renderCart(c));
          })
          .catch(() => { btn.textContent = t('cart_error'); })
          .finally(() => {
            setTimeout(() => { btn.textContent = t('cart_add'); btn.classList.remove('added'); btn.disabled = false; }, 1200);
          });
      }
      // Cart line qty change
      if (e.target.matches('[data-cart-qty]')) {
        const line = parseInt(e.target.dataset.cartQty);
        const delta = parseInt(e.target.dataset.delta);
        fetchCart().then(c => {
          const item = c.items[line - 1];
          if (item) updateCart(line, item.quantity + delta).then(c2 => renderCart(c2));
        });
      }
    });
  }

  // ============================================================
  // Product detail page - qty controls and image swap
  // ============================================================
  function bindProductPage() {
    const page = document.querySelector('[data-product-page]');
    if (!page) return;
    const qtyInput = page.querySelector('[data-qty-input]');
    page.querySelector('[data-qty-down]')?.addEventListener('click', () => {
      qtyInput.value = Math.max(1, parseInt(qtyInput.value) - 1);
    });
    page.querySelector('[data-qty-up]')?.addEventListener('click', () => {
      qtyInput.value = parseInt(qtyInput.value) + 1;
    });

    // Image gallery
    page.querySelectorAll('[data-swap-image]').forEach(thumb => {
      thumb.addEventListener('click', () => {
        page.querySelectorAll('[data-swap-image]').forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        const mainImg = document.getElementById('detail-main-img');
        if (mainImg) mainImg.src = thumb.dataset.swapImage;
      });
    });

    // Intercept product form submit to use cart API and open drawer
    const form = page.querySelector('[data-product-form]');
    if (form) {
      form.addEventListener('submit', e => {
        e.preventDefault();
        const variantId = form.querySelector('input[name="id"]').value;
        const qty = parseInt(form.querySelector('input[name="quantity"]').value);
        addToCart(variantId, qty).then(() => openCart());
      });
    }
  }

  // ============================================================
  // Fitment Results page - read query params, fetch matching parts
  // ============================================================
  function renderFitmentResults() {
    const wrap = document.querySelector('[data-fitment-results]');
    if (!wrap) return;

    const params = new URLSearchParams(window.location.search);
    const year = params.get('year');
    const make = params.get('make');
    const model = params.get('model');

    const headingEl = wrap.querySelector('[data-result-heading]');
    const eyebrowEl = wrap.querySelector('[data-result-eyebrow]');
    const countEl = wrap.querySelector('[data-result-count]');
    const chipsEl = wrap.querySelector('[data-result-chips]');
    const gridEl = wrap.querySelector('[data-result-grid]');

    if (!year || !make) {
      if (headingEl) headingEl.textContent = t('results_heading_default');
      if (eyebrowEl) eyebrowEl.textContent = t('results_eyebrow_default');
      return;
    }

    const label = year + ' ' + make + (model ? ' ' + model : '');
    if (headingEl) headingEl.textContent = t('results_heading_prefix', {vehicle: label});
    if (eyebrowEl) eyebrowEl.textContent = t('results_eyebrow');
    if (chipsEl) {
      let chips =
        '<span class="filter-chip"><span class="filter-chip-label">' + t('ymm_year') + '</span>' + year + '</span>' +
        '<span class="filter-chip"><span class="filter-chip-label">' + t('ymm_make') + '</span>' + make + '</span>';
      if (model) {
        chips += '<span class="filter-chip"><span class="filter-chip-label">' + t('ymm_model') + '</span>' + model + '</span>';
      }
      chipsEl.innerHTML = chips;
    }

    loadFitmentIndex().then(idx => {
      if (!idx) {
        if (gridEl) gridEl.innerHTML = '<div class="no-results" style="grid-column:1/-1">' + t('results_load_error') + '</div>';
        return;
      }

      // Model is optional: if omitted, aggregate handles across every model for this Year+Make.
      const makeMap = (idx.fitment[year] || {})[make] || {};
      let handles = model
        ? (makeMap[model] || [])
        : Object.keys(makeMap).reduce((acc, mdl) => acc.concat(makeMap[mdl]), []);
      handles = Array.from(new Set(handles));

      if (handles.length === 0) {
        if (countEl) countEl.textContent = t('results_count', {count: 0});
        gridEl.innerHTML = '<div class="no-results" style="grid-column:1/-1"><h3>' + t('results_no_match_title') + '</h3><p>' + t('results_no_match_body') + '</p></div>';
        return;
      }

      // Fetch product data directly by handle via Shopify's product JSON API.
      // We can't use an inlined Liquid catalog: collections.all loops cap at 50 products.
      const MAX = 48;
      const shown = handles.slice(0, MAX);
      if (gridEl) gridEl.innerHTML = '<div class="no-results" style="grid-column:1/-1">' + t('results_loading') + '</div>';
      Promise.all(shown.map(h =>
        fetch('/products/' + encodeURIComponent(h) + '.js')
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      )).then(items => {
        const products = items.filter(Boolean);
        if (products.length === 0) {
          if (countEl) countEl.textContent = t('results_count', {count: 0});
          gridEl.innerHTML = '<div class="no-results" style="grid-column:1/-1"><h3>' + t('results_no_match_title') + '</h3><p>' + t('results_no_match_body') + '</p></div>';
          return;
        }

        // Availability + sort toolbar. Client-side: we already hold the full product objects from /products/{handle}.js.
        const toolbar = wrap.querySelector('[data-result-toolbar]');
        const sortSel = wrap.querySelector('[data-result-sort]');
        const availBtns = Array.from(wrap.querySelectorAll('[data-avail]'));
        const inCount = products.filter(p => p.available).length;
        const plus = handles.length > MAX ? '+' : '';
        let avail = 'all';

        function apply() {
          let list = products.slice();
          if (avail === 'in') list = list.filter(p => p.available);
          if (avail === 'out') list = list.filter(p => !p.available);
          const sort = sortSel ? sortSel.value : 'default';
          if (sort === 'price-asc') list.sort((a, b) => a.price - b.price);
          if (sort === 'price-desc') list.sort((a, b) => b.price - a.price);
          if (sort === 'title') list.sort((a, b) => String(a.title).localeCompare(String(b.title)));
          if (countEl) countEl.textContent = t('results_count', {count: list.length + (avail === 'all' ? plus : '')});
          gridEl.innerHTML = list.length
            ? list.map(productCardHTML).join('')
            : '<div class="no-results" style="grid-column:1/-1"><h3>' + t('results_nothing_title') + '</h3><p>' + (avail === 'in' ? t('results_nothing_body_in') : t('results_nothing_body_out')) + '</p></div>';
        }

        availBtns.forEach(b => {
          const mode = b.dataset.avail;
          if (mode === 'in') b.textContent = t('results_in_stock_count', {count: inCount});
          if (mode === 'out') b.textContent = t('results_out_stock_count', {count: products.length - inCount});
          b.addEventListener('click', () => {
            avail = mode;
            availBtns.forEach(x => x.classList.toggle('is-cur', x === b));
            apply();
          });
        });
        if (sortSel) sortSel.addEventListener('change', apply);

        if (toolbar) toolbar.style.display = '';
        apply();
      });
    });
  }

  // Derive the car make from a product title (vendor is the store name, not the brand).
  var TEP_BRANDS = ['Toyota','Honda','Nissan','Ford','Chevrolet','Chevy','Ram','Dodge','Jeep','GMC','Infiniti','Mitsubishi','Mercedes','Genesis','Promaster','Sprinter','BMW','Outlander','Silverado'];
  function makeFromTitle(title) {
    const t = String(title || '').toLowerCase();
    for (const b of TEP_BRANDS) {
      if (t.indexOf(b.toLowerCase()) !== -1) {
        if (b === 'Chevy' || b === 'Silverado') return 'Chevrolet';
        if (b === 'Outlander') return 'Mitsubishi';
        return b;
      }
    }
    return '';
  }

  // Renders a product card (.tep-pcard) from a Shopify /products/{handle}.js object.
  // Prices are in cents; featured_image is a URL string; tags is an array.
  function productCardHTML(p) {
    const url = '/products/' + p.handle;
    let img = p.featured_image || (p.images && p.images[0]) || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    const variant = (p.variants && p.variants[0]) || {};
    const sku = variant.sku || '';
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const make = makeFromTitle(p.title) || ((Array.isArray(p.tags) && p.tags.length) ? p.tags[0] : '');
    const priceHTML = onSale
      ? '<span class="tep-price__sale">$' + (p.price/100).toFixed(2) + '</span><span class="tep-price__regular">$' + (p.compare_at_price/100).toFixed(2) + '</span><span class="tep-price__save">' + t('product_save', {amount: '$' + ((p.compare_at_price - p.price)/100).toFixed(2)}) + '</span>'
      : '<span class="tep-price__plain">$' + (p.price/100).toFixed(2) + '</span>';
    const badge = !p.available
      ? '<span class="tep-badge tep-badge--soldout">' + t('product_sold_out') + '</span>'
      : (onSale ? '<span class="tep-badge tep-badge--sale">' + t('product_sale') + '</span>' : '');
    const imgPart = img
      ? '<img src="' + img + '" alt="' + escapeHTML(p.title) + '" loading="lazy">'
      : '<span class="placeholder">' + t('product_no_image') + '</span>';
    const addBtn = p.available
      ? '<button class="tep-btn tep-btn--primary tep-btn--sm tep-btn--block" data-quick-add data-variant-id="' + variant.id + '" onclick="event.preventDefault(); event.stopPropagation();">' + t('product_add_to_cart') + '</button>'
      : '<button class="tep-btn tep-btn--secondary tep-btn--sm tep-btn--block" disabled>' + t('product_notify_me') + '</button>';
    return '<a href="' + url + '" class="tep-pcard">' +
      '<div class="tep-pcard__media">' +
        '<div class="tep-pcard__badges">' + badge + '</div>' +
        imgPart +
        (!p.available ? '<div class="tep-pcard__soldout"><span class="tep-badge tep-badge--soldout">' + t('product_sold_out') + '</span></div>' : '') +
      '</div>' +
      '<div class="tep-pcard__body">' +
        (make ? '<button type="button" class="tep-pcard__make" title="See all ' + escapeHTML(make) + ' parts" onclick="event.preventDefault();event.stopPropagation();window.location.assign(\'' + makeLink(make) + '\');">' + escapeHTML(make) + '</button>' : '') +
        '<h3 class="tep-pcard__title">' + escapeHTML(p.title) + '</h3>' +
        (sku ? '<div class="tep-pcard__sku">#' + escapeHTML(sku) + '</div>' : '') +
        '<div class="tep-pcard__spacer"></div>' +
        '<div class="tep-pcard__foot">' +
          '<span class="tep-price tep-price--sm">' + priceHTML + '</span>' +
          addBtn +
        '</div>' +
      '</div>' +
    '</a>';
  }

  function escapeHTML(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  // ============================================================
  // Fitment-aware Search + Predictive Autocomplete
  // Bridges the search bar to the fitment index + a part classifier so a
  // loose query ("2018 infinity bumper") returns EVERY fitting part, grouped
  // by part type. Falls back to native Shopify results for non-vehicle queries.
  // ============================================================
  var MAKE_ALIASES = {
    infiniti:'Infiniti', infinity:'Infiniti', infinit:'Infiniti', infinti:'Infiniti', infinitti:'Infiniti',
    chevy:'Chevrolet', chev:'Chevrolet', chevorlet:'Chevrolet', chevrolet:'Chevrolet',
    mercedes:'Mercedes-Benz', benz:'Mercedes-Benz', 'mercedes-benz':'Mercedes-Benz', merc:'Mercedes-Benz',
    vw:'Volkswagen', volkswagon:'Volkswagen', volkswagen:'Volkswagen',
    toyata:'Toyota', toyota:'Toyota', hyundia:'Hyundai', hyundai:'Hyundai',
    mistubishi:'Mitsubishi', mitsibishi:'Mitsubishi', mitsubishi:'Mitsubishi',
    ram:'Dodge', dodge:'Dodge', gmc:'GMC', nissan:'Nissan', honda:'Honda', ford:'Ford'
  };
  // The fitment index folds Ram into Dodge and combines a couple of makes into one collection.
  // Display name (search heading) + the correct collection/search link per make.
  var MAKE_DISPLAY = { 'Dodge': 'Dodge & Ram' };
  var MAKE_LINK = {
    'toyota': '/collections/toyota', 'nissan': '/collections/nissan', 'honda': '/collections/honda',
    'chevrolet': '/collections/chevrolet-gmc', 'chevy': '/collections/chevrolet-gmc', 'gmc': '/collections/chevrolet-gmc',
    'infiniti': '/collections/infiniti', 'ford': '/collections/ford',
    'dodge': '/collections/dodge-ram', 'ram': '/collections/dodge-ram', 'mitsubishi': '/collections/mitsubishi'
  };
  function makeLink(make) {
    var hh = String(make || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return MAKE_LINK[hh] || ('/search?type=product&q=' + encodeURIComponent(make));
  }
  // Static make tokens for a SYNCHRONOUS "does this query name a make?" check (runs before the
  // fitment index loads, so native results are hidden instantly and other makes can never leak in).
  var MAKE_TOKEN_SET = (function () {
    var s = {}; for (var k in MAKE_ALIASES) s[k] = 1;
    ['toyota','honda','nissan','ford','chevrolet','gmc','ram','dodge','jeep','infiniti','mitsubishi','mercedes','benz','genesis','bmw','lexus','acura','subaru','mazda','hyundai','kia','cadillac','buick','lincoln','chrysler','volkswagen','audi','volvo','freightliner','sprinter','promaster'].forEach(function (m) { s[m] = 1; });
    return s;
  })();
  function queryHasMake(q) {
    return String(q || '').toLowerCase().split(/[^a-z0-9-]+/).some(function (tk) { return MAKE_TOKEN_SET[tk]; });
  }
  // Part synonyms: query word -> extra word(s) that should also count as a match.
  var PART_SYNONYMS = { bumper:['fascia'], fascia:['bumper'], cover:['cover'], headlamp:['headlight'], taillight:['tail light'], rim:['wheel'] };
  // Merchandising order for grouped results when a part intent is present.
  var PART_ORDER = ['Bumper Cover','Bumper Kit','Grille','Grille Trim','Bumper Bracket','Bumper Reinforcement','Bumper Trim & Molding','Bumper Valance','Bumper End Cap','Fog Light Cover','Signal & Fog Light','Headlight','Headlight Trim','Tail Light','Fender','Fender Flare','Fender Liner','Mirror','Radiator Support','Radiator Support Kit','Door Molding & Trim','Door Shell Panel','Body Side Molding','Trunk Lid & Tailgate','Air Intake'];
  var STOPWORDS = {'for':1,'the':1,'and':1,'with':1,'oem':1,'new':1,'part':1,'parts':1,'auto':1,'car':1,'a':1,'of':1};

  function lev(a, b) {
    a = a || ''; b = b || ''; var m = a.length, n = b.length;
    if (!m) return n; if (!n) return m;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= n; j++) prev[j] = j;
    for (i = 1; i <= m; i++) {
      cur[0] = i;
      for (j = 1; j <= n; j++) {
        var c = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + c);
      }
      for (j = 0; j <= n; j++) prev[j] = cur[j];
    }
    return prev[n];
  }
  function resolveMake(token, makes) {
    if (!token) return null;
    var tl = token.toLowerCase();
    if (MAKE_ALIASES[tl]) return MAKE_ALIASES[tl];
    var i;
    for (i = 0; i < makes.length; i++) { if (makes[i].toLowerCase() === tl) return makes[i]; }
    for (i = 0; i < makes.length; i++) { var ml = makes[i].toLowerCase(); if (ml.length >= 5 && lev(tl, ml) <= 1) return makes[i]; }
    return null;
  }
  function parseSearchQuery(q, idx) {
    var tokens = String(q || '').toLowerCase().split(/[^a-z0-9-]+/).filter(Boolean);
    var years = [], make = null, model = null, kws = [], used = {}, i;
    var yearSet = idx._yearSet; if (!yearSet) { yearSet = {}; (idx.years || []).forEach(function (y) { yearSet[String(y)] = 1; }); idx._yearSet = yearSet; }
    tokens.forEach(function (tk, ix) {
      if (/^(19|20)\d\d$/.test(tk)) { years.push(tk); used[ix] = 1; }                  // full year: 2018
      else if (/^\d{2}$/.test(tk)) {                                                     // 2-digit year: "18" -> 2018
        var yy = parseInt(tk, 10); var full = String(yy <= 49 ? 2000 + yy : 1900 + yy);
        if (yearSet[full]) { years.push(full); used[ix] = 1; }                           // only if it's a real catalog year
      }
    });
    for (i = 0; i < tokens.length; i++) { if (used[i]) continue; var mk = resolveMake(tokens[i], idx.makes || []); if (mk) { make = mk; used[i] = 1; break; } }
    // No make typed? Infer it from a distinctive MODEL name ("promaster"->Dodge, "rav4"->Toyota),
    // but only when that model's lead word belongs to exactly ONE make (so "1500"/"sport" never mis-guess).
    if (!make) {
      var rev = idx._modelFirstWordMakes;
      if (!rev) {
        rev = {}; var mm0 = idx.makeModels || {};
        Object.keys(mm0).forEach(function (mk2) {
          (mm0[mk2] || []).forEach(function (model0) {
            var fw = String(model0).toLowerCase().split(/\s+/)[0];
            if (fw && fw.length >= 2 && !/^\d+$/.test(fw)) { (rev[fw] = rev[fw] || {})[mk2] = 1; }
          });
        });
        idx._modelFirstWordMakes = rev;
      }
      for (i = 0; i < tokens.length; i++) {
        if (used[i]) continue;
        var cand = rev[tokens[i]];
        if (cand) { var ks = Object.keys(cand); if (ks.length === 1) { make = ks[0]; break; } }
      }
    }
    if (make) {
      var models = (idx.makeModels && idx.makeModels[make]) ? idx.makeModels[make] : [];
      // LONGEST model first, matched as a consecutive word run (2026-07-13), so "corolla cross"
      // resolves to "Corolla Cross" (never "Corolla" + a leftover keyword), "rav4 prime" to
      // "Rav4 Prime" — while a bare "corolla" still resolves to "Corolla".
      var byLen = models.slice().sort(function (a, b) { return b.length - a.length; });
      outer:
      for (var mi = 0; mi < byLen.length; mi++) {
        var mwords = byLen[mi].toLowerCase().split(/\s+/);
        for (i = 0; i + mwords.length <= tokens.length; i++) {
          var ok = true;
          for (var w = 0; w < mwords.length; w++) { if (used[i + w] || tokens[i + w] !== mwords[w]) { ok = false; break; } }
          if (ok) { model = byLen[mi]; for (w = 0; w < mwords.length; w++) used[i + w] = 1; break outer; }
        }
      }
      if (!model) {
        var mlow = models.map(function (m) { return m.toLowerCase(); });
        for (i = 0; i < tokens.length; i++) {
          if (used[i]) continue; var tl = tokens[i];
          // word-match a model, but only if it UNIQUELY identifies one model — so "promaster"
          // does not pin to just "ProMaster 1500"; it stays a keyword and matches 1500/2500/3500.
          var wm = []; for (var j = 0; j < mlow.length; j++) { if (mlow[j].split(/\s+/).indexOf(tl) !== -1) wm.push(j); }
          if (wm.length === 1) { model = models[wm[0]]; used[i] = 1; break; }
        }
      }
    }
    for (i = 0; i < tokens.length; i++) { if (used[i]) continue; var t2 = tokens[i]; if (t2.length < 2 || STOPWORDS[t2]) continue; kws.push(t2); }
    return { years: years, make: make, model: model, keywords: kws, raw: q };
  }
  function keywordHay(m) { return (m.t + ' ' + m.ty + ' ' + m.pp).toLowerCase(); }
  function metaMatchesKeywords(m, kws) {
    if (!kws.length) return true;
    var hay = keywordHay(m);
    return kws.every(function (k) {
      if (hay.indexOf(k) !== -1) return true;
      var syn = PART_SYNONYMS[k]; if (syn) { for (var s = 0; s < syn.length; s++) { if (hay.indexOf(syn[s]) !== -1) return true; } }
      return false;
    });
  }
  function vehicleHandleSet(idx, parsed) {
    var yrs = parsed.years.length ? parsed.years : Object.keys(idx.fitment || {});
    var set = {};
    yrs.forEach(function (y) {
      var mm = (idx.fitment[y] || {})[parsed.make]; if (!mm) return;
      Object.keys(mm).forEach(function (mdl) {
        if (parsed.model) {
          // EXACT model match (2026-07-13). Substring matching mixed distinct vehicles that
          // share a name prefix: Corolla/Corolla Cross, Rav4/Rav4 Prime, Rogue/Rogue Sport,
          // Transit/Transit Connect, Outlander/Outlander Sport, Highlander/Grand Highlander.
          // parseSearchQuery resolves the LONGEST model named in the query, so equality is safe.
          if (mdl.toLowerCase() !== parsed.model.toLowerCase()) return;
        }
        mm[mdl].forEach(function (h) { set[h] = 1; });
      });
    });
    return Object.keys(set);
  }

  // Lazy product hydration (skeleton -> live card) so we can group hundreds of parts cheaply.
  var _prodCache = {};
  function fetchProduct(h) {
    if (_prodCache[h]) return _prodCache[h];
    _prodCache[h] = fetch('/products/' + encodeURIComponent(h) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    return _prodCache[h];
  }
  // Live availability + price for a set of handles (also pre-warms the card cache -> instant render).
  function fetchProductMaps(handles) {
    return Promise.all(handles.map(function (h) {
      return fetchProduct(h).then(function (p) { return [h, !!(p && p.available), (p ? p.price : 0)]; });
    })).then(function (rows) { var av = {}, price = {}; rows.forEach(function (x) { av[x[0]] = x[1]; price[x[0]] = x[2]; }); return { av: av, price: price }; });
  }
  function hydrateGrid(grid, handles) {
    grid.innerHTML = handles.map(function (h) { return '<div class="tep-pcard tep-pcard--skel" data-h="' + escapeHTML(h) + '"></div>'; }).join('');
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-h]'));
    if (!('IntersectionObserver' in window)) { cards.forEach(function (el) { fetchProduct(el.getAttribute('data-h')).then(function (p) { if (p) el.outerHTML = productCardHTML(p); else el.remove(); }); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return; var el = en.target; io.unobserve(el);
        fetchProduct(el.getAttribute('data-h')).then(function (p) { if (p) el.outerHTML = productCardHTML(p); else el.remove(); });
      });
    }, { rootMargin: '500px' });
    cards.forEach(function (c) { io.observe(c); });
  }

  function buildGroups(handles, meta, kws) {
    var groups = {};
    handles.forEach(function (h) { var m = meta[h]; if (!m) return; (groups[m.pp] = groups[m.pp] || []).push(h); });
    var names = Object.keys(groups);
    names.sort(function (a, b) {
      var pa = kws.some(function (k) { return a.toLowerCase().indexOf(k) !== -1; }) ? 0 : 1;
      var pb = kws.some(function (k) { return b.toLowerCase().indexOf(k) !== -1; }) ? 0 : 1;
      if (pa !== pb) return pa - pb;
      var oa = PART_ORDER.indexOf(a); if (oa < 0) oa = PART_ORDER.length;
      var ob = PART_ORDER.indexOf(b); if (ob < 0) ob = PART_ORDER.length;
      if (oa !== ob) return oa - ob;
      return groups[b].length - groups[a].length;
    });
    return { groups: groups, names: names };
  }

  // A self-contained YMM finder block, wired by initYMMFinders() after injection.
  // target='search' keeps the user inside the unified grouped search and carries the part keyword.
  function ymmFinderHTML(defaultMake, keyword, cta) {
    return '<div class="sf-filter sf-filter--ymm fzs-ymm" data-ymm-finder data-context="inline" data-ymm-target="search"' +
      ' data-default-make="' + escapeHTML(defaultMake || '') + '" data-keyword="' + escapeHTML(keyword || '') + '">' +
      '<div class="fzs-ymm-row">' +
        '<select data-ymm-year aria-label="Year"><option value="">Year</option></select>' +
        '<select data-ymm-make disabled aria-label="Make"><option value="">Make</option></select>' +
        '<select data-ymm-model disabled aria-label="Model"><option value="">Model</option></select>' +
        '<button type="button" class="tep-btn tep-btn--primary tep-btn--sm" data-ymm-submit>' + escapeHTML(cta || 'Show parts that fit') + '</button>' +
      '</div></div>';
  }

  // Shared renderer: ONE flat grid of every exact-fit part, so Sort by applies across ALL of
  // them (not within per-type sections). Part type is an opt-in narrow in the left rail, next to
  // the WORKING in/out-of-stock toggle (counts come from the catalog index, so they match).
  function renderGroupedParts(host, allHandles, meta, kws, vlabel, opts) {
    opts = opts || {};
    injectSearchStyles();
    var availMap = opts.availMap || null, priceMap = opts.priceMap || null;
    function avOf(h) { return availMap ? !!availMap[h] : !!(meta[h] && meta[h].av); }
    var inH = allHandles.filter(avOf);
    var outH = allHandles.filter(function (h) { return !avOf(h); });
    var avail = 'all', sortMode = 'rel', typeFilter = 'all';

    // Results (right column): just the heading + the single grid.
    host.innerHTML =
      '<div class="fzs-head"><div class="fzs-eyebrow">' + escapeHTML(opts.eyebrow || 'EXACT-FIT RESULTS') + '</div>' +
      '<h2 class="fzs-title" data-fzs-title></h2></div><div data-fzs-groups></div>';
    host.hidden = false;
    var titleEl = host.querySelector('[data-fzs-title]'), groupsEl = host.querySelector('[data-fzs-groups]');
    // The server-rendered tab title shows the NATIVE text-search count (e.g. "154 results"),
    // which disagrees with the exact-fit view; mirror the on-page heading into the tab instead.
    var tabSuffix = (function () { var t0 = document.title, i = t0.lastIndexOf(' – '); return i > -1 ? t0.slice(i) : ' – Texas-e-parts'; })();

    // Filters + sort live in the LEFT rail (a slot the page provides); fall back to the top if absent.
    var controls = document.querySelector('[data-grouped-controls]');
    if (!controls) { controls = document.createElement('div'); controls.setAttribute('data-grouped-controls', ''); host.insertBefore(controls, host.firstChild); }
    controls.innerHTML =
      '<div class="gc-block"><h4 class="gc-h">Sort by</h4>' +
        '<select class="gc-sort" data-gc-sort>' +
          '<option value="rel">Relevance</option><option value="price-asc">Price: Low to High</option>' +
          '<option value="price-desc">Price: High to Low</option><option value="az">Name: A&ndash;Z</option>' +
        '</select></div>' +
      '<div class="gc-block"><h4 class="gc-h">Availability</h4>' +
        '<button type="button" class="gc-av is-cur" data-gc-av="all">All <b>' + allHandles.length + '</b></button>' +
        '<button type="button" class="gc-av" data-gc-av="in">In stock <b>' + inH.length + '</b></button>' +
        '<button type="button" class="gc-av" data-gc-av="out">Out of stock <b>' + outH.length + '</b></button>' +
      '</div>' +
      '<div class="gc-block"><h4 class="gc-h">Part type</h4><div data-gc-types></div></div>';
    var typesEl = controls.querySelector('[data-gc-types]');

    function sortArr(arr) {
      if (sortMode === 'price-asc') arr.sort(function (a, b) { return (priceMap ? (priceMap[a] || 0) : 0) - (priceMap ? (priceMap[b] || 0) : 0); });
      else if (sortMode === 'price-desc') arr.sort(function (a, b) { return (priceMap ? (priceMap[b] || 0) : 0) - (priceMap ? (priceMap[a] || 0) : 0); });
      else if (sortMode === 'az') arr.sort(function (a, b) { return String(meta[a] ? meta[a].t : '').localeCompare(String(meta[b] ? meta[b].t : '')); });
      return arr;
    }
    function paint() {
      var visAll = avail === 'in' ? inH : (avail === 'out' ? outH : allHandles);
      var gAll = buildGroups(visAll, meta, kws);
      // Left part-type list (counts reflect the current availability filter).
      typesEl.innerHTML = '<button type="button" class="gc-type' + (typeFilter === 'all' ? ' is-cur' : '') + '" data-gc-type="all">All parts <b>' + visAll.length + '</b></button>' +
        gAll.names.map(function (pp) { return '<button type="button" class="gc-type' + (typeFilter === pp ? ' is-cur' : '') + '" data-gc-type="' + escapeHTML(pp) + '">' + escapeHTML(pp) + ' <b>' + gAll.groups[pp].length + '</b></button>'; }).join('');
      typesEl.querySelectorAll('[data-gc-type]').forEach(function (b) { b.addEventListener('click', function () { typeFilter = b.getAttribute('data-gc-type'); paint(); }); });
      // ONE flat grid: all matching parts together so the chosen sort ranks across every part.
      // Relevance keeps the part-type priority order (keyword-matched type first, then PART_ORDER).
      // A part-type pick in the left rail narrows this same grid — no per-type sections.
      var flat = [];
      if (typeFilter === 'all') { gAll.names.forEach(function (pp) { gAll.groups[pp].forEach(function (h) { flat.push(h); }); }); }
      else if (gAll.groups[typeFilter]) { flat = gAll.groups[typeFilter].slice(); }
      var typeSuffix = (typeFilter !== 'all') ? (' &mdash; ' + escapeHTML(typeFilter)) : '';
      titleEl.innerHTML = opts.collection
        ? (flat.length + ' ' + escapeHTML(vlabel) + ' parts' + typeSuffix)
        : (flat.length + ' parts fit ' + escapeHTML(vlabel) + (typeSuffix || (kws.length ? (' matching &ldquo;' + escapeHTML(kws.join(' ')) + '&rdquo;') : '')));
      document.title = titleEl.textContent + tabSuffix;
      if (!flat.length) { groupsEl.innerHTML = '<div class="no-results"><h3>Nothing here</h3><p>Try a different filter.</p></div>'; return; }
      groupsEl.innerHTML = '<div class="sf-pgrid sf-pgrid--4" data-flat-grid></div>';
      hydrateGrid(groupsEl.querySelector('[data-flat-grid]'), sortArr(flat));
    }
    controls.querySelector('[data-gc-sort]').addEventListener('change', function () { sortMode = this.value; paint(); });
    controls.querySelectorAll('[data-gc-av]').forEach(function (b) { b.addEventListener('click', function () { avail = b.getAttribute('data-gc-av'); controls.querySelectorAll('[data-gc-av]').forEach(function (x) { x.classList.toggle('is-cur', x === b); }); typeFilter = 'all'; paint(); }); });
    paint();
  }

  // Point the page's sidebar "Find by Vehicle" finder back into the unified search, carrying the part keyword.
  function setAsideNarrow(make, keyword) {
    var f = document.querySelector('.sf-filters [data-ymm-finder]');
    if (f) { f.dataset.ymmTarget = 'search'; f.dataset.keyword = keyword || ''; }
  }

  // Ambiguous query (vehicle not clear) -> force a vehicle pick so the result can't be wrong.
  function renderVehicleGate(host, parsed) {
    injectSearchStyles();
    var partLabel = parsed.keywords.length ? parsed.keywords.join(' ') : 'parts';
    host.innerHTML =
      '<div class="fzs-gate">' +
        '<div class="fzs-eyebrow">PICK YOUR VEHICLE</div>' +
        '<h2 class="fzs-gate__title">Which vehicle? We’ll show the ' + escapeHTML(partLabel) + ' that fit — guaranteed.</h2>' +
        '<p class="fzs-gate__sub">Choose Year, Make and Model so you never see the wrong part.</p>' +
        ymmFinderHTML(parsed.make || '', parsed.keywords.join(' '), 'Show parts that fit') +
      '</div>';
    host.hidden = false;
    // Native count in the tab title is meaningless while the gate is up — neutralize it.
    var gi = document.title.lastIndexOf(' – ');
    document.title = 'Pick your vehicle' + (gi > -1 ? document.title.slice(gi) : ' – Texas-e-parts');
    initYMMFinders();
  }

  function enhanceFitmentSearch() {
    var host = document.querySelector('[data-fitment-search]');
    if (!host) return;
    var q = new URLSearchParams(location.search).get('q') || '';
    if (!q.trim()) return;
    var native = document.querySelector('[data-native-results]');
    // A make is clearly named -> hide native IMMEDIATELY (sync, before the index loads) so the loose
    // text-search results can never leak other makes (e.g. Toyota parts on an Infiniti search).
    var hadMake = queryHasMake(q);
    if (hadMake && native) native.style.display = 'none';
    Promise.all([loadFitmentIndex(), loadSearchMeta()]).then(function (res) {
      var idx = res[0], meta = res[1];
      if (!idx || !meta) { if (hadMake && native) native.style.display = ''; return; } // restore only if our data failed
      var parsed = parseSearchQuery(q, idx);
      function hideNative() { if (native) native.style.display = 'none'; }
      if (!parsed.make) {
        // No make: a part-like word forces a vehicle pick; a SKU/random falls through to native search.
        var partLike = parsed.keywords.some(function (k) { return /^[a-z][a-z-]{2,}$/.test(k); });
        if (partLike) { renderVehicleGate(host, parsed); hideNative(); }
        return;
      }
      var handles = vehicleHandleSet(idx, parsed);
      if (!handles.length) { renderVehicleGate(host, parsed); hideNative(); return; }
      var kws = parsed.keywords;
      var matched = handles.filter(function (h) { var m = meta[h]; return m && metaMatchesKeywords(m, kws); });
      var show = matched.length ? matched : handles;     // keyword matched nothing -> show all the vehicle's parts
      var viewKws = matched.length ? kws : [];
      var dmake = MAKE_DISPLAY[parsed.make] || parsed.make;
      var vlabel = (parsed.years.length ? parsed.years.join('/') + ' ' : '') + dmake + (parsed.model ? (' ' + parsed.model) : '');
      hideNative();
      injectSearchStyles();
      setAsideNarrow(parsed.make, kws.join(' '));
      host.hidden = false;
      host.innerHTML = '<div class="fzs-loading">Loading ' + show.length + ' parts…</div>';
      // Pull LIVE availability + price for the matched parts (also pre-warms the card cache), then render with real counts.
      fetchProductMaps(show).then(function (maps) {
        renderGroupedParts(host, show, meta, viewKws, vlabel, { make: parsed.make, keyword: kws.join(' '), availMap: maps.av, priceMap: maps.price });
      });
    });
  }

  // Make-collection pages get the SAME grouped experience as search:
  // categorized by part type + working in/out-of-stock toggle + "pick your vehicle" narrow.
  function fetchCollectionData(handle) {
    var handles = [], av = {}, price = {};
    function page(p) {
      return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + p)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (!d || !d.products || !d.products.length) return { handles: handles, av: av, price: price };
          d.products.forEach(function (pr) {
            handles.push(pr.handle);
            var vs = pr.variants || [];
            av[pr.handle] = vs.some(function (v) { return v.available; });
            price[pr.handle] = parseFloat((vs[0] && vs[0].price) || 0) || 0;
          });
          return d.products.length === 250 ? page(p + 1) : { handles: handles, av: av, price: price };
        }).catch(function () { return { handles: handles, av: av, price: price }; });
    }
    return page(1);
  }
  function enhanceCollection() {
    var host = document.querySelector('[data-collection-grouped]');
    if (!host) return;
    var handle = host.getAttribute('data-collection-grouped');
    var title = host.getAttribute('data-collection-title') || handle;
    var makeDefault = host.getAttribute('data-collection-make') || title;
    Promise.all([loadSearchMeta(), fetchCollectionData(handle)]).then(function (res) {
      var meta = res[0], data = res[1];
      if (!meta || !data.handles.length) return; // leave native grid in place
      var known = data.handles.filter(function (h) { return meta[h]; });
      if (!known.length) return;
      setAsideNarrow(makeDefault, '');
      renderGroupedParts(host, known, meta, [], title, {
        make: makeDefault, keyword: '', collection: true, eyebrow: 'ALL ' + title.toUpperCase() + ' PARTS', availMap: data.av, priceMap: data.price
      });
      var native = document.querySelector('[data-collection-native]'); if (native) native.style.display = 'none';
    });
  }

  function initPredictiveSearch() {
    var inputs = Array.prototype.slice.call(document.querySelectorAll('input[name="q"]'));
    inputs.forEach(function (input) {
      if (input.dataset.tepPredict) return; input.dataset.tepPredict = '1';
      input.setAttribute('autocomplete', 'off');
      var wrap = input.parentNode; if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
      var box = document.createElement('div'); box.className = 'tep-predict'; box.hidden = true; wrap.appendChild(box);
      var tmr = null;
      function close() { box.hidden = true; }
      input.addEventListener('input', function () {
        var q = input.value.trim(); if (tmr) clearTimeout(tmr);
        if (q.length < 2) { close(); return; }
        tmr = setTimeout(function () { injectSearchStyles(); runPredict(q, box); }, 140);
      });
      input.addEventListener('focus', function () { if (input.value.trim().length >= 2 && box.innerHTML) box.hidden = false; });
      document.addEventListener('click', function (e) { if (!box.contains(e.target) && e.target !== input) close(); });
    });
  }
  function runPredict(q, box) {
    Promise.all([loadFitmentIndex(), loadSearchMeta()]).then(function (res) {
      var idx = res[0], meta = res[1]; if (!idx || !meta) return;
      var parsed = parseSearchQuery(q, idx);
      var pool = parsed.make ? vehicleHandleSet(idx, parsed) : Object.keys(meta);
      var hits = pool.filter(function (h) { var m = meta[h]; return m && metaMatchesKeywords(m, parsed.keywords); });
      var groups = {}; hits.forEach(function (h) { var pp = meta[h].pp; (groups[pp] = groups[pp] || []).push(h); });
      function ppScore(pp) { var primary = parsed.keywords.some(function (k) { return pp.toLowerCase().indexOf(k) !== -1; }) ? 0 : 1; var oi = PART_ORDER.indexOf(pp); if (oi < 0) oi = PART_ORDER.length; return [primary, oi]; }
      var gAll = Object.keys(groups).sort(function (a, b) { var sa = ppScore(a), sb = ppScore(b); if (sa[0] !== sb[0]) return sa[0] - sb[0]; if (sa[1] !== sb[1]) return sa[1] - sb[1]; return groups[b].length - groups[a].length; });
      var gChips = gAll.slice(0, 6).map(function (pp) { return '<a class="tep-predict__chip" href="/search?type=product&q=' + encodeURIComponent(q) + '">' + escapeHTML(pp) + ' <b>' + groups[pp].length + '</b></a>'; }).join('');
      var ordered = []; gAll.forEach(function (pp) { groups[pp].forEach(function (h) { ordered.push(h); }); });
      var top = ordered.slice(0, 6).map(function (h) { var m = meta[h]; return '<a class="tep-predict__item" href="/products/' + encodeURIComponent(h) + '"><span class="tep-predict__t">' + escapeHTML(m.t) + '</span><span class="tep-predict__ty">' + escapeHTML(m.pp) + '</span></a>'; }).join('');
      var label = parsed.make ? ((parsed.years[0] ? parsed.years[0] + ' ' : '') + parsed.make) : q;
      var all = '<a class="tep-predict__all" href="/search?type=product&q=' + encodeURIComponent(q) + '">See all ' + hits.length + ' results for ' + escapeHTML(label) + ' &rarr;</a>';
      box.innerHTML = (gChips ? '<div class="tep-predict__chips">' + gChips + '</div>' : '') + (top || '<div class="tep-predict__empty">No matches yet — try a year, make, or part.</div>') + all;
      box.hidden = false;
    });
  }

  var _stylesDone = false;
  function injectSearchStyles() {
    if (_stylesDone) return; _stylesDone = true;
    var css = ''
      + '.fzs-head{margin:0 0 18px}.fzs-eyebrow{font-family:var(--font-ui,inherit);font-weight:700;font-size:12px;letter-spacing:.08em;color:var(--brand,#e4002b)}'
      + '.fzs-title{font-size:22px;margin:4px 0 12px;color:var(--text-strong,#111)}'
      + '.fzs-chips{display:flex;flex-wrap:wrap;gap:8px}'
      + '.fzs-chip{cursor:pointer;border:1px solid var(--border-default,#ddd);background:var(--surface-page,#fff);border-radius:999px;padding:6px 12px;font-size:13px;font-weight:600;color:var(--text-strong,#111)}'
      + '.fzs-chip b{color:var(--brand,#e4002b);margin-left:2px}.fzs-chip:hover{border-color:var(--brand,#e4002b)}'
      + '.fzs-group{margin:22px 0}.fzs-group__title{font-size:16px;text-transform:uppercase;letter-spacing:.03em;border-bottom:1px solid var(--border-default,#eee);padding-bottom:8px;margin:0 0 14px;color:var(--text-strong,#111)}.fzs-group__title span{color:var(--text-muted,#888);font-weight:500}'
      + '.tep-pcard--skel{min-height:300px;border-radius:12px;background:linear-gradient(100deg,#f2f2f2 30%,#e9e9e9 50%,#f2f2f2 70%);background-size:200% 100%;animation:tepskel 1.2s infinite}'
      + '@keyframes tepskel{0%{background-position:200% 0}100%{background-position:-200% 0}}'
      + '.tep-predict{position:absolute;top:calc(100% + 6px);left:0;right:0;z-index:60;background:#fff;border:1px solid var(--border-default,#e3e3e3);border-radius:12px;box-shadow:0 12px 40px rgba(0,0,0,.16);overflow:hidden;max-height:70vh;overflow-y:auto}'
      + '.tep-predict__chips{display:flex;flex-wrap:wrap;gap:6px;padding:10px 12px;border-bottom:1px solid var(--border-default,#eee);background:var(--surface-subtle,#fafafa)}'
      + '.tep-predict__chip{font-size:12px;font-weight:600;border:1px solid var(--border-default,#ddd);border-radius:999px;padding:4px 10px;color:var(--text-strong,#111);text-decoration:none}.tep-predict__chip b{color:var(--brand,#e4002b)}'
      + '.tep-predict__item{display:flex;justify-content:space-between;gap:12px;padding:10px 14px;text-decoration:none;color:var(--text-strong,#111);border-bottom:1px solid #f3f3f3}.tep-predict__item:hover{background:var(--surface-subtle,#f7f7f7)}'
      + '.tep-predict__t{font-size:13px;line-height:1.3}.tep-predict__ty{font-size:11px;color:var(--text-muted,#999);white-space:nowrap;align-self:center}'
      + '.tep-predict__all{display:block;padding:12px 14px;font-weight:700;font-size:13px;color:var(--brand,#e4002b);text-decoration:none;text-align:center}'
      + '.tep-predict__empty{padding:14px;color:var(--text-muted,#999);font-size:13px}'
      + '.fzs-avail{display:flex;gap:8px;margin:14px 0 4px;flex-wrap:wrap}'
      + '.fzs-av{cursor:pointer;border:1px solid var(--border-default,#ddd);background:var(--surface-page,#fff);border-radius:8px;padding:7px 14px;font-size:13px;font-weight:600;color:var(--text-strong,#111)}'
      + '.fzs-av b{color:var(--text-muted,#888);margin-left:3px}.fzs-av.is-cur{background:var(--text-strong,#111);color:#fff;border-color:var(--text-strong,#111)}.fzs-av.is-cur b{color:#fff}'
      + '.fzs-narrow{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin:2px 0 12px;padding:12px 14px;background:var(--surface-subtle,#faf7f5);border:1px solid var(--border-default,#eee);border-radius:12px}'
      + '.fzs-narrow__label{font-weight:700;font-size:13px;color:var(--text-strong,#111)}'
      + '.fzs-ymm-row{display:flex;gap:8px;flex-wrap:wrap;align-items:center}'
      + '.fzs-ymm-row select{padding:8px 10px;border:1px solid var(--border-default,#ddd);border-radius:8px;background:#fff;font-size:13px;min-width:108px}'
      + '.fzs-gate{max-width:640px;margin:8px 0 30px;padding:26px;border:1px solid var(--border-default,#eee);border-radius:16px;background:var(--surface-subtle,#faf7f5)}'
      + '.fzs-gate__title{font-size:22px;margin:6px 0 6px;color:var(--text-strong,#111)}.fzs-gate__sub{color:var(--text-muted,#777);margin:0 0 16px;font-size:14px}'
      + '.tep-pcard__make{cursor:pointer;display:inline-block;padding:2px 7px;margin:0 -7px 2px;border:0;background:transparent;-webkit-appearance:none;appearance:none;font:inherit;font-weight:700;font-size:11px;letter-spacing:.04em;text-transform:uppercase;color:var(--brand,#e4002b);border-radius:6px;text-decoration:none;transition:background .12s}'
      + '.tep-pcard__make:hover{background:rgba(228,0,43,.12);text-decoration:underline}.tep-pcard__make:after{content:" ›";opacity:.55;font-weight:700}'
      + '.fzs-loading{padding:48px 0;text-align:center;color:var(--text-muted,#888);font-size:14px}'
      + '.gc-block{margin:0 0 20px}.gc-h{font-size:11px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--text-muted,#888);margin:0 0 8px}'
      + '.gc-sort{width:100%;padding:8px 10px;border:1px solid var(--border-default,#ddd);border-radius:8px;background:var(--surface-page,#fff);font-size:13px;color:var(--text-strong,#111)}'
      + '.gc-av,.gc-type{display:flex;justify-content:space-between;align-items:center;gap:8px;width:100%;text-align:left;cursor:pointer;border:1px solid transparent;background:transparent;border-radius:8px;padding:7px 10px;margin:1px 0;font-size:13.5px;font-weight:500;color:var(--text-strong,#111)}'
      + '.gc-av b,.gc-type b{color:var(--text-muted,#999);font-weight:600;font-size:12px}'
      + '.gc-av:hover,.gc-type:hover{background:var(--surface-subtle,#f5f5f5)}'
      + '.gc-av.is-cur,.gc-type.is-cur{background:var(--text-strong,#111);color:#fff;border-color:var(--text-strong,#111)}.gc-av.is-cur b,.gc-type.is-cur b{color:rgba(255,255,255,.75)}';
    var s = document.createElement('style'); s.id = 'tep-search-css'; s.textContent = css; document.head.appendChild(s);
  }

  // Make badge on a product card -> that make's collection (all its parts).
  // Capture phase so we intercept before the card's own product link.
  function initMakeLinks() {
    document.body.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-make-link]') : null;
      if (!el) return;
      e.preventDefault(); e.stopPropagation();
      var h = el.getAttribute('data-make-link');
      if (h) window.location.href = '/collections/' + h;
    }, true);
  }

  // ============================================================
  // Init
  // ============================================================
  document.addEventListener('DOMContentLoaded', () => {
    initYMMFinders();
    bindGarage();
    bindCart();
    bindSaveProductFitment();
    bindProductPage();
    renderGarageChip();
    renderFitmentResults();
    enhanceFitmentSearch();
    enhanceCollection();
    initPredictiveSearch();
    // Pre-load cart count on page load
    fetchCart().then(c => {
      const countEl = document.querySelector('[data-cart-count]');
      if (countEl) countEl.textContent = c.item_count;
    });
  });

  // Expose useful globals for debugging
  window.TXEP = { state, openCart, closeCart, openGarage, saveVehicle, loadFitmentIndex, loadSearchMeta, enhanceFitmentSearch, parseSearchQuery };
})();

